import firebase, { database } from 'firebase/app'
import 'firebase/firestore'
import 'firebase/database'
import _ from 'lodash'
import format from 'date-fns/format'
import parse from 'date-fns/parse'
import { globalfunc } from '../globalfunc.js'
import axios from 'axios'
import { getByUser, getCompany, getTeamByCompany, getUserAll, invalidateMemberAndTeamCaches } from './core/cacheService'
import {
  buildImportEvaluationFromReviewItems,
  buildImportMutationPlan,
  buildImportPreview,
} from './utils/memberImport'
import { getCompanyTimezone, toCompanyDate } from './utils/companyTimezone'
import { createTeamWithBossParent, findTeamByName, maybeAutoSetTeamParent } from './utils/memberTeamResolve'

const apiUrl = process.env.VUE_APP_API_URL

export const importMemberDeps = {
  getCompany: async (companyId: string) => getCompany(companyId),
  getTeamByCompany: async (companyId: string) => getTeamByCompany(companyId),
  getUserAll: async (companyId: string) => getUserAll(companyId),
  getArchivedMembers: async (companyId: string) => {
    return (await firebase.database().ref('users_employee_exit').orderByChild('company').equalTo(companyId).once('value')).val() || {}
  },
  getTeamImportAliases: async (companyId: string) => {
    return (await firebase.database().ref(`company/${companyId}/team_import_aliases`).once('value')).val() || {}
  },
  createDatabaseKey: (path: string) => firebase.database().ref(path).push().key || '',
  setDatabaseValue: (path: string, value: any) => firebase.database().ref(path).set(value),
  updateDatabaseValue: (path: string, value: any) => firebase.database().ref(path).update(value),
  batchDatabaseUpdate: (updates: Record<string, any>) => firebase.database().ref().update(updates),
  invalidateMemberAndTeamCaches: (companyId: string) => invalidateMemberAndTeamCaches(companyId),
  addLogs: async (dispatch: any, payload: any) => dispatch('authen/addLogs', payload, { root: true }),
  resolveCallerIp: async (): Promise<string> => {
    try {
      const response = await axios.get('https://api.ipify.org?format=json')
      return (response && response.data && response.data.ip) || ''
    } catch (error) {
      return ''
    }
  },
}

const ucfirst = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
}
const isEmptyObject = (value: object) => {
  return Object.keys(value).length === 0
}

const normalizeImportString = (value: any) => {
  if (value === undefined || value === null) return ''
  return String(value).trim()
}

const normalizeImportEmail = (value: any) => {
  const normalized = normalizeImportString(value)
  if (!normalized) return ''

  if (normalized.indexOf('@') !== -1) {
    return normalized.toLowerCase().replace(/\s+/g, '')
  }

  const isPhoneLike = /^(?:\+|00)?[\d\s().-]+$/.test(normalized)
  if (!isPhoneLike) {
    return normalized.toLowerCase().replace(/\s+/g, '')
  }

  let phone = normalized.replace(/[\s().-]+/g, '')
  if (phone.startsWith('00')) {
    phone = `+${phone.slice(2)}`
  }
  if (phone.startsWith('+')) {
    return `+${phone.slice(1).replace(/\D+/g, '')}`
  }
  return phone.replace(/\D+/g, '')
}

const normalizeImportHeader = (value: any) => {
  return normalizeImportString(value)
    .toLowerCase()
    .replace(/[()]/g, ' ')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
}

const matchesImportHeaderAlias = (cell: string, alias: string) => {
  return cell === alias
    || cell.startsWith(`${alias} `)
    || cell.includes(` ${alias} `)
}

const findImportHeaderIndex = (header: any[], aliases: string[], fallback = -1) => {
  const normalizedHeader = header.map(normalizeImportHeader)
  const aliasList = aliases.map(normalizeImportHeader)
  const index = normalizedHeader.findIndex((cell: string) => {
    return aliasList.some((alias) => matchesImportHeaderAlias(cell, alias))
  })
  return index !== -1 ? index : fallback
}

const isValidImportEmail = (email: string) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

const isValidImportPhone = (phone: string) => {
  return /^\+?[1-9]\d{6,14}$/.test(phone)
}

const isValidImportIdentifier = (value: string) => {
  return isValidImportEmail(value) || isValidImportPhone(value)
}

const buildImportName = (firstName: string, lastName: string) => {
  return [normalizeImportString(firstName), normalizeImportString(lastName)]
    .filter(Boolean)
    .join(' ')
}

const IMPORT_LANGUAGE_ALIASES: Record<string, string> = {
  en: 'en',
  eng: 'en',
  english: 'en',
  'อังกฤษ': 'en',
  th: 'th',
  thai: 'th',
  'ไทย': 'th',
  my: 'my',
  burmese: 'my',
  myanmar: 'my',
  'မြန်မာ': 'my',
  ms: 'ms',
  malay: 'ms',
  'bahasa malaysia': 'ms',
  'zh hk': 'zh-HK',
  'zh-hk': 'zh-HK',
  'traditional chinese': 'zh-HK',
  'chinese traditional hong kong': 'zh-HK',
  'chinese traditional': 'zh-HK',
  'pt br': 'pt-br',
  'pt-br': 'pt-br',
  'brazilian portuguese': 'pt-br',
}

const IMPORT_USER_TYPE_ALIASES: Record<string, string> = {
  user: 'user',
  member: 'user',
  employee: 'user',
  admin: 'admin',
  administrator: 'admin',
  developer: 'developer',
  dev: 'developer',
  hr: 'hr',
  'human resources': 'hr',
  leader: 'leadership',
  leadership: 'leadership',
  manager: 'leadership',
  superleader: 'superleader',
  'super leader': 'superleader',
  superadmin: 'superadmin',
  'super admin': 'superadmin',
  ghost: 'ghost',
}

const normalizeImportLanguage = (value: any, fallback: string) => {
  const normalized = normalizeImportHeader(value)
  if (!normalized) return fallback
  if (IMPORT_LANGUAGE_ALIASES[normalized]) return IMPORT_LANGUAGE_ALIASES[normalized]
  return fallback
}

const normalizeImportUserType = (value: any, fallback: string) => {
  const normalized = normalizeImportHeader(value)
  if (!normalized) return fallback
  if (IMPORT_USER_TYPE_ALIASES[normalized]) return IMPORT_USER_TYPE_ALIASES[normalized]
  return fallback
}

const normalizeImportCountry = (value: any, fallback: string) => {
  const normalized = normalizeImportString(value)
  if (!normalized) return fallback
  if (normalized.toLowerCase() === 'thailand') return 'TH'
  return normalized
}

const offsetNumberToString = (value: number) => {
  if (isNaN(value) || value < -12 || value > 14) {
    return null
  }

  const sign = value >= 0 ? '+' : '-'
  const absolute = Math.abs(value)
  const hours = Math.floor(absolute)
  const fraction = Number((absolute - hours).toFixed(2))
  const minuteLookup: Record<string, number> = {
    '0': 0,
    '0.25': 15,
    '0.5': 30,
    '0.75': 45,
  }
  const minutes = minuteLookup[String(fraction)]
  if (minutes === undefined) {
    return null
  }

  return `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

const normalizeImportTimezone = (input: any, fallback: any) => {
  const fallbackNumber = typeof fallback === 'number' ? fallback : Number(fallback)
  const fallbackOffset = typeof fallback === 'string'
    ? offsetStringToNumber(fallback)
    : fallbackNumber
  const fallbackValue = offsetNumberToString(fallbackOffset)
    || offsetNumberToString(fallbackNumber)
    || '+07:00'
  const raw = normalizeImportString(input)

  if (!raw) {
    return { value: fallbackValue }
  }

  const colonMatch = raw.match(/^([+-]?)(\d{1,2}):(\d{2})$/)
  if (colonMatch) {
    const sign = colonMatch[1] === '-' ? -1 : 1
    const hours = parseInt(colonMatch[2], 10)
    const minutes = parseInt(colonMatch[3], 10)
    if ([0, 15, 30, 45].indexOf(minutes) === -1) {
      return {
        value: fallbackValue,
        error: 'Timezone must use 00, 15, 30, or 45 minute increments.',
      }
    }
    const decimal = sign * (hours + minutes / 60)
    const normalized = offsetNumberToString(decimal)
    if (normalized) {
      return { value: normalized }
    }
    return {
      value: fallbackValue,
      error: 'Timezone must be between UTC-12 and UTC+14.',
    }
  }

  const numeric = Number(raw)
  if (!isNaN(numeric)) {
    const normalized = offsetNumberToString(numeric)
    if (normalized) {
      return { value: normalized }
    }
  }

  return {
    value: fallbackValue,
    error: 'Timezone must be between UTC-12 and UTC+14.',
  }
}

function offsetStringToNumber(value: any) {
  const raw = normalizeImportString(value)
  if (!raw) return 0
  const colonMatch = raw.match(/^([+-]?)(\d{1,2}):(\d{2})$/)
  if (colonMatch) {
    const sign = colonMatch[1] === '-' ? -1 : 1
    const hours = parseInt(colonMatch[2], 10)
    const minutes = parseInt(colonMatch[3], 10)
    return sign * (hours + minutes / 60)
  }
  const numeric = Number(raw)
  return isNaN(numeric) ? 0 : numeric
}

const isLeapYear = (year: number) => {
  return year % 400 === 0 || (year % 4 === 0 && year % 100 !== 0)
}

const isValidDateParts = (year: number, month: number, day: number) => {
  if (!year || month < 1 || month > 12 || day < 1) return false
  const daysInMonth = [31, isLeapYear(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]
  return day <= daysInMonth[month - 1]
}

const normalizeImportDate = (input: any) => {
  const raw = normalizeImportString(input)
  if (!raw) {
    return { value: '' }
  }

  let year = 0
  let month = 0
  let day = 0

  if (/^\d{4}[-/]\d{1,2}[-/]\d{1,2}$/.test(raw)) {
    const parts = raw.split(/[-/]/)
    year = parseInt(parts[0], 10)
    month = parseInt(parts[1], 10)
    day = parseInt(parts[2], 10)
  } else if (/^\d{1,2}[-/]\d{1,2}[-/]\d{4}$/.test(raw)) {
    const parts = raw.split(/[-/]/)
    const first = parseInt(parts[0], 10)
    const second = parseInt(parts[1], 10)
    const third = parseInt(parts[2], 10)
    year = third
    if (first > 12 && second <= 12) {
      month = second
      day = first
    } else {
      month = first
      day = second
    }
  } else {
    return {
      value: '',
      error: 'Date must use YYYY-MM-DD or MM/DD/YYYY.',
    }
  }

  if (!isValidDateParts(year, month, day)) {
    return {
      value: '',
      error: 'Date is invalid.',
    }
  }

  return {
    value: `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
  }
}

const normalizeImportBirthday = (input: any) => {
  const raw = normalizeImportString(input)
  if (!raw) {
    return { value: '' }
  }

  let month = 0
  let day = 0

  if (/^\d{1,2}[-/]\d{1,2}$/.test(raw)) {
    const parts = raw.split(/[-/]/)
    const first = parseInt(parts[0], 10)
    const second = parseInt(parts[1], 10)
    if (first > 12 && second <= 12) {
      month = second
      day = first
    } else {
      month = first
      day = second
    }
  } else if (/^\d{4}[-/]\d{1,2}[-/]\d{1,2}$/.test(raw)) {
    const parts = raw.split(/[-/]/)
    month = parseInt(parts[1], 10)
    day = parseInt(parts[2], 10)
  } else if (/^\d{1,2}[-/]\d{1,2}[-/]\d{4}$/.test(raw)) {
    const parts = raw.split(/[-/]/)
    const first = parseInt(parts[0], 10)
    const second = parseInt(parts[1], 10)
    if (first > 12 && second <= 12) {
      month = second
      day = first
    } else {
      month = first
      day = second
    }
  } else {
    return {
      value: '',
      error: 'Birthday must use MM-DD. Full dates are accepted, but only month and day are stored.',
    }
  }

  if (!isValidDateParts(2000, month, day)) {
    return {
      value: '',
      error: 'Birthday is invalid.',
    }
  }

  return {
    value: `${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
  }
}

const normalizeStoredBirthday = (input: any) => {
  const normalized = normalizeImportBirthday(input)
  return normalized.value || normalizeImportString(input)
}

const normalizeStoredWorkdate = (input: any) => {
  const normalized = normalizeImportDate(input)
  return normalized.value || normalizeImportString(input)
}

export default {
  namespaced: true,
  state: {
    selectedSso: {},
    dataSettings: { 'webhook_logs': [] },
    directorySyncSettings: {},
    hasDirectorySyncSettings: false,
    apiKeys: {},
    apiKeyDev: {},
    apiKeyProd: {},
    apiLogs: { page: 1, data: [], results: [], error: false, errorMsg: '' },
    resultsSimulate: [],
    membersSimulate: [],
    membersSimulateAll: [],
    loadingSimulateTable: true,
    inputSearchSimulate: '',
    size_page: 10,
    sortfieldsimulate: 'value',
    sortordersimulate: 'asc',
    pagesimulate: 1,
    resultsView: [],
    membersView: [],
    membersViewAll: [],
    loadingViewTable: true,
    inputSearchView: '',
    sortfieldview: 'value',
    sortorderview: 'asc',
    pageview: 1,
    tokenLength: 30,

    reviewUpdates: [],
    reviewUpdatesAll: [],
    reviewUpdatesTotal: 0,
    reviewUpdatesPage: 1,
    reviewUpdatesByKey: [],
    pageReviewUpdates: 1,
    sortfieldreviewupdates: 'email',
    sortordereviewupdates: 'asc',
    reviewUpdatesLoading: false,
    _reviewUpdates: [],
    _reviewUpdatesAll: [],
    reviewUpdatesIgnorePendingStatus: false, // true for dev mode

    // import members — the groups here are the single canonical review
    // dataset; search filtering is a pure view-layer concern in members.vue.
    importMembersTableDataGroups: [],
    importMembersTableDataGroupsTotal: 0,
  },
  mutations: {
    selectedSso(state: any, result: object) {
      state.selectedSso = result
    },
    setApiKeys(state: any, result: any) {
      state.apiKeys = result
    },
    setApiKeyDev(state: any, result: any) {
      state.apiKeyDev = result
    },
    setApiKeyProd(state: any, result: any) {
      state.apiKeyProd = result
    },
    unsetApiKeyDev(state: any, result: any) {
      const apiKeyDev = {
        generate: false,
        token: '',
        mockTaken: [],
        showingStatus: false,
      }
      state.apiKeyDev = apiKeyDev;
    },
    unsetApiKeyProd(state: any, result: any) {
      const apiKeyProd = {
        generate: false,
        token: '',
        mockTaken: [],
        showingStatus: false,
      }
      state.apiKeyProd = apiKeyProd;
    },
    setApiLogs(state: any, result: any) {
      state.apiLogs = result
    },
    setDataIntegrationSettings(state: any, result: any) {
      state.dataSettings = result
    },
    setHasDirectorySyncSettings(state: any, result: any) {
      state.hasDirectorySyncSettings = result
    },
    setDirectorySyncSettings(state: any, result: any) {
      state.directorySyncSettings = result
    },
    settokenTemp(state: any) {
      state.dataSettings.token_temp = globalfunc.generateToken(state.tokenLength)
    },
    setResultsSimulate(state: any, result: any) {
      state.resultsSimulate = result
    },
    setMemberSimulate(state: any, value: any) {
      state.membersSimulate = value
    },
    setMemberSimulateAll(state: any, value: any) {
      state.membersSimulateAll = value
    },
    setLoadingSimulateTable(state: any, result: any) {
      state.loadingSimulateTable = result
    },
    setinputSearchSimulate(state: any, value: any) {
      state.inputSearchSimulate = value.keyword
    },
    setPageSimulate(state: any, result: any) {
      state.pagesimulate = result
    },
    setSortfieldSimulate(state: any, result: any) {
      state.sortfieldsimulate = result
    },
    setSortorderSimulate(state: any, result: any) {
      state.sortordersimulate = result
    },
    setResultsView(state: any, result: any) {
      state.resultsView = result
    },
    setMemberView(state: any, value: any) {
      state.membersView = value
    },
    setMemberViewAll(state: any, value: any) {
      state.membersViewAll = value
    },
    setLoadingViewTable(state: any, result: any) {
      state.loadingViewTable = result
    },
    setinputSearchView(state: any, value: any) {
      state.inputSearchView = value.keyword
    },
    setPageView(state: any, result: any) {
      state.pageview = result
    },
    setSortfieldView(state: any, result: any) {
      state.sortfieldview = result
    },
    setSortorderView(state: any, result: any) {
      state.sortorderview = result
    },
    setReviewUpdates(state: any, result: any) {
      state.reviewUpdates = result
    },
    setReviewUpdatesAll(state: any, result: any) {
      state.reviewUpdatesAll = result
    },
    setReviewUpdatesLoading(state: any, result: any) {
      state.reviewUpdatesLoading = result
    },
    setReviewUpdatesTotal(state: any, result: any) {
      state.reviewUpdatesTotal = result
    },
    reviewUpdatesPage(state: any, result: any) {
      state.reviewUpdatesPage = result
    },
    setReviewUpdatesByKey(state: any, result: any) {
      state.reviewUpdatesByKey = result
    },
    setPageReviewUpdates(state: any, result: any) {
      state.pageReviewUpdates = result
    },
    setSortfieldReviewUpdates(state:any,result:any) {
      state.sortfieldreviewupdates = result
    },
    setSortorderReviewUpdates(state:any,result:any) {
      state.reviewupdates = result
    },
    setImportMembersTableDataGroups(state:any,result:any) {
      state.importMembersTableDataGroups = result
    },
    setImportMembersTableDataGroupsTotal(state:any,result:any) {
      state.importMembersTableDataGroupsTotal = result
    },
  },
  actions: {

    async getUserCompany({ commit, rootState, dispatch, state }: any) {
      return await getCompany(rootState.authen.user.company)//(await firebase.database().ref(`company/${rootState.authen.user.company}`).once('value')).val()
    },

    /* sso */

    // _createSsoBySaml()

    // used for unauthenticated story
    async getSelectedSsoByEmail({ commit, rootState, dispatch, state }: any, email: string) {
      let user;
      if(email){
        const emailKey = email.replace(/\./g, '||')
        if (rootState.main.allUser[emailKey]) {
          user = rootState.main.allUser[emailKey];
        } else {
          user = await getByUser(email)//(await firebase.database().ref(`/users/${emailKey}`).once('value')).val();
        }
      }
      let selectedSso = null
      if (user) {
        const company = await getCompany(user.company)//(await firebase.database().ref(`company/${user.company}`).once('value')).val();
        const sso_configuration = company.sso_configuration || {};
        const options = sso_configuration.options || [];
        selectedSso = _.find(options, ['selected', true]);
      }
      return selectedSso
    },

    async createDefaultSsoConfiguration({ commit, rootState, dispatch, state }: any, values: object) {
      const sso_configuration = Object.assign({
        enabled: false,
        options: []
      }, values)

      await firebase.database().ref(`company/${rootState.authen.user.company}`).update({
        "sso_configuration": sso_configuration,
        "updated": format(new Date(), "YYYY-MM-DD HH:mm:ss")
      })

      return sso_configuration
    },

    async getSsoConfiguration({ commit, rootState, dispatch, state }: any) {
      let company = await dispatch('getUserCompany')
      let sso_configuration = company.sso_configuration || {}
      if (isEmptyObject(sso_configuration)) {
        sso_configuration = await dispatch('createDefaultSsoConfiguration', { enabled: true })
      }
      return sso_configuration
    },

    async saveSso({ commit, rootState, dispatch, state }: any, selectedSso: any) {
      let sso_configuration = await dispatch('getSsoConfiguration')
      if (!sso_configuration.options) sso_configuration.options = []

      let found = false
      for (let i in sso_configuration.options) {
        sso_configuration.options[i].selected = false
        if (sso_configuration.options[i].ssoProviderId == selectedSso.ssoProviderId) {
          sso_configuration.options[i] = selectedSso
          found = true
        }
      }

      if (!found) {
        sso_configuration.options.push(selectedSso)
      }

      // Create or Update (Inconsistency 2 way apis)
      // console.log(found, 'found')
      if (found) {

        const asyncFunction = (resolve, reject) => {
          axios.put(`${apiUrl}/identity-platform/update-config-provider/${selectedSso.ssoProviderId}`, {
            "displayName": selectedSso.connectionName,
            "enabled": selectedSso.enabled,
            "idpEntityId": selectedSso.idpEntityId,
            "ssoURL": selectedSso.ssoUrl,
            "x509Certificates": [selectedSso.certificate],
            "rpEntityId": selectedSso.spEntityId,
            "callbackURL": selectedSso.acsUrl
          })
            .then(function (response) {
              // console.log(response, 'response');
              resolve(response)
            })
            .catch(function (error) {
              // console.log(error, 'error');
              reject(error)
            });
        }

        const fn = new Promise((resolve, reject) => {
          asyncFunction(resolve, reject)
        })

        await fn;

      } else {

        const asyncFunction = (resolve, reject) => {
          axios.post(`${apiUrl}/identity-platform/create-config-provider`, {
            "displayName": selectedSso.connectionName,
            "enabled": selectedSso.enabled,
            "providerId": selectedSso.ssoProviderId,
            "idpEntityId": selectedSso.idpEntityId,
            "ssoURL": selectedSso.ssoUrl,
            "x509Certificates": [selectedSso.certificate],
            "rpEntityId": selectedSso.spEntityId,
            "callbackURL": selectedSso.acsUrl
          })
            .then(function (response) {
              // console.log(response, 'response');
              resolve(response)
            })
            .catch(function (error) {
              // console.log(error, 'error');
              reject(error)
            });
        }

        const fn = new Promise((resolve, reject) => {
          asyncFunction(resolve, reject)
        })

        await fn;
      }


      // @TODO update only option
      await firebase.database().ref(`company/${rootState.authen.user.company}`).update({
        "sso_configuration": sso_configuration,
        "updated": format(new Date(), "YYYY-MM-DD HH:mm:ss")
      })
    },

    async deleteSso({ commit, rootState, dispatch, state }: any, selectedSso: any) {
      let sso_configuration = await dispatch('getSsoConfiguration')
      if (!sso_configuration.options) sso_configuration.options = []

      let found = false
      let index = ''
      for (let i in sso_configuration.options) {
        if (sso_configuration.options[i].ssoProviderId == selectedSso.ssoProviderId) {
          sso_configuration.options[i] = selectedSso
          found = true
          index = i
        }
      }

      if (found) {
        const ssoProviderId = sso_configuration.options[index].ssoProviderId
        delete sso_configuration.options[index]

        const asyncFunction = (resolve, reject) => {
          axios.delete(`${apiUrl}/identity-platform/delete-config-provider/${ssoProviderId}`)
            .then(function (response) {
              // console.log(response, 'response');
              resolve(response)
            })
            .catch(function (error) {
              // console.log(error, 'error');
              reject(error)
            });
        }

        const fn = new Promise((resolve, reject) => {
          asyncFunction(resolve, reject)
        })

        await fn;
      }

      // @TODO update only option
      await firebase.database().ref(`company/${rootState.authen.user.company}`).update({
        "sso_configuration": sso_configuration,
        "updated": format(new Date(), "YYYY-MM-DD HH:mm:ss")
      })
    },

    /* api key */

    async getApiKeys({ commit, rootState, dispatch, state }: any) {
      // @todo use common function
      let company = await getCompany(rootState.authen.user.company)//(await firebase.database().ref(`company/${rootState.authen.user.company}`).once('value')).val()
      const emptyApiKeyObj = {
        generate: false,
        token: "",
        mockTaken: new Array,
        showingStatus: false,
      }
      let apiKeys = company.api_keys || {};

      const auto_create_api_key_dev = company.auto_create_api_key_dev || false;

      let apiKeyDev = Object.assign({}, emptyApiKeyObj);
      let apiKeyProd = Object.assign({}, emptyApiKeyObj);

      const assignApiKeyValue = (targetObj, valueObj) => {
        if (valueObj) {
          targetObj.generate = true;
          targetObj.token = valueObj.api_key;

          let token = new Array;
          for (let i: number = 0; i < targetObj.token.length; i++) {
            token.push(i);
          }
          targetObj.mockTaken = token;
        }
      }

      if (!auto_create_api_key_dev && !apiKeys.dev) {
        await dispatch('createApiKey', 'dev');

        // reload new data for company and apiKeys
        company = await getCompany(rootState.authen.user.company)//(await firebase.database().ref(`company/${rootState.authen.user.company}`).once('value')).val()
        apiKeys = company.api_keys || {};

        // @todo update company flags
        await firebase.database().ref(`company/${rootState.authen.user.company}`).update({
          "auto_create_api_key_dev": true,
          "updated": format(new Date(), "YYYY-MM-DD HH:mm:ss")
        })
      }

      if (apiKeys.dev) {
        assignApiKeyValue(apiKeyDev, apiKeys.dev);
      }

      if (apiKeys.prod) {
        assignApiKeyValue(apiKeyProd, apiKeys.prod);
      }

      commit('setApiKeys', apiKeys)
      commit('setApiKeyDev', apiKeyDev);
      commit('setApiKeyProd', apiKeyProd);
    },

    async createApiKey({ commit, rootState, dispatch, state }: any, stageName: string) {
      const asyncFunction = (resolve) => {
        // console.log(stageName, 'stageName');
        axios.post(`${apiUrl}/apigateway/create-api-key`, {
          "companyId": rootState.authen.user.company,
          "usagePlanId": "3shwv6",
          "restApiId": "ln1buvf68h",
          "stageName": stageName
        })
          .then(function (response) {
            // console.log(response, 'response');
            const data = response.data;
            if (data.statusCode != 500) {
              const apiKeyObj = {
                generate: true,
                token: data.value,
                mockTaken: [],
                showingStatus: false,
              }
              commit(`setApiKey${ucfirst(stageName)}`, apiKeyObj);
              resolve(apiKeyObj);
            }
          })
          .catch(function (error) {
            console.log(error, 'error');
          });
      }

      const fn = new Promise((resolve, reject) => {
        asyncFunction(resolve)
      })

      await fn;
    },

    async removeApiKey({ commit, rootState, dispatch, state }: any, stageName: string) {
      const company = await getCompany(rootState.authen.user.company)//(await firebase.database().ref(`company/${rootState.authen.user.company}`).once('value')).val()
      try {
        const apiKey = company.api_keys[stageName].api_key
        const asyncFunction = (resolve) => {
          axios.post(`${apiUrl}/apigateway/delete-api-key`, {
            "apiKey": apiKey
          })
            .then(function (response) {
              commit(`unsetApiKey${ucfirst(stageName)}`)
              resolve();
            })
            .catch(function (error) {
              console.log(error, 'error');
              resolve();
            })
        }
        const fn = new Promise((resolve, reject) => {
          asyncFunction(resolve)
        })
        await fn;
      } catch (e) {
        console.log(e);
      }
    },

    async getApiLogs({ commit, rootState, dispatch, state }: any) {
      let results: any = { page: 1, data: [], results: [] };

      const companyId = rootState.authen.user.company;

      const timezone = await getCompanyTimezone(companyId)
      let dateNow = toCompanyDate(new Date(), timezone);
      let endDate = dateNow.getTime();

      let diffDay: number;
      diffDay = dateNow.getDate() - 7;
      dateNow.setDate(diffDay);

      let startDate = dateNow.getTime();

      // @see https://docs.aws.amazon.com/AWSJavaScriptSDK/latest/AWS/CloudWatchLogs.html#filterLogEvents-property
      axios.post(`${apiUrl}/logs/filter-log-events`, {
        "logGroupName": process.env.VUE_APP_API_LOG_GROUP,
        "endTime": endDate,
        //"limit": 10000,
        "logStreamNamePrefix": companyId + "||",
        "startTime": startDate
        //"interleaved": true
      })
        .then(function (response) {
          try {
            const events = response.data.events || [];

            const monthNames = ["January", "February", "March", "April", "May", "June",
              "July", "August", "September", "October", "November", "December"
            ];

            const langMapping = {
              "dev": "Developement",
              "prod": "Production",
              "getMedalById": "Happily Medal: Get Medal",
              "getRecognitionsForMember": "Happily Recognition - Get Recognitions for Member",
              "createRecognition": "Happily Recognition - Create Recognition"
            }

            const pad = (num, size) => {
                var s = "00" + num;
                return s.substring(s.length-size);
            }

            events.forEach(event => {
              const rawData = JSON.parse(event.message);

              // @fixed 13 digits timestamp with milliseconds value
              const date = new Date(event.ingestionTime);
              const YYYY = date.getFullYear();
              const MMMM = monthNames[date.getMonth()];
              const d = date.getDate();
              const h = ("0" + (date.getHours() >= 12 ? (date.getHours() - 12) : date.getHours())).slice(-2);
              const mm = pad(date.getMinutes(), 2);
              const A = date.getHours() >= 12 ? "PM" : "AM";

              const displayedDate = `${MMMM} ${d}, ${YYYY}`
              const displayedTime = `${h}:${mm} ${A}`

              const data = {
                // date: format(parse(event.ingestionTime),'MMMM d, YYYY'),
                // time: format(parse(event.ingestionTime),'h:mm A'),
                eventId: event.eventId,
                logStreamName: event.logStreamName,
                timestamp: event.timestamp,
                date: displayedDate,
                time: displayedTime,
                operation: langMapping[rawData.operation],
                status: rawData.status ? 'Success' : 'Failed',
                deployment: langMapping[rawData.deployment]
              };

              results.data.push(data)
            });

            commit('setApiLogs', results)
            dispatch('pageCurrentLogChange', 1)
          } catch (e) {
            console.log(e, 'e');
          }
        })
        .catch(function (error) {
          console.log(error, 'error');
          const apiLogs = { page: 1, data: [], results: [], error: true, errorMsg: error.message }
          commit('setApiLogs', apiLogs)
        });

    },

    pageCurrentLogChange({ commit, rootState, dispatch, state }: any, value: number) {
      let sizePage = 10;
      let start_row = (value - 1) * sizePage
      let end_row = ((value - 1) * sizePage) + sizePage

      state.apiLogs.page = value;
      state.apiLogs.results = state.apiLogs.data.slice(start_row, end_row)
      commit('setApiLogs', state.apiLogs)
    },

    async getDataIntregrationSetting({ commit, rootState, dispatch, state }: any, user: any) {
      let results: any
      let dateNow = new Date()

      await firebase.database().ref(`integration_settings/${rootState.authen.user.company}`).once('value', (snapshot) => {
        if (snapshot.val()) {
          results = snapshot.val()
        } else {
          let objData = {
            webhook: 'https://integrations-bv43tjyzrq-de.a.run.app/users',
            token: globalfunc.generateToken(state.tokenLength),
            sync_type: 'normal',
            live: false,
            abbreviate_team_name: false,
            updated: format(dateNow, 'YYYY-MM-DD HH:mm'),
            created: format(dateNow, 'YYYY-MM-DD HH:mm'),

          }
          firebase.database().ref(`/integration_settings`).child(rootState.authen.user.company).set(objData)
          results = objData
        }
      });
      results.webhook_logs = new Array()
      await firebase.database().ref('integration_request_logs')
        .orderByChild('comp_id')
        .equalTo(rootState.authen.user.company)
        .once('value', snapshot => {
          snapshot.forEach((doc): any => {
            let data = doc.val()
            data.parse_date = format(parse(data.batch_ct), 'MMMM d, YYYY')
            data.parse_time = format(parse(data.batch_ct), 'h:mm A')
            data.content = JSON.stringify(data.request_data)
            results.webhook_logs.push(data)
          });
        });

      results.token_temp = results.token
      // json_encode
      commit('setDataIntegrationSettings', results)

      // @todo connect realtime database
      commit('setHasDirectorySyncSettings', false);
      commit('setDirectorySyncSettings', {
        'endpoint': 'https://api.workos.com/scim/v2.0/UNxMntQuAss69Pns/?aadOptscim062020',
        'token': 'se_WWwndlq80MmrxLfrz8bbOBfEN',
        'sync_type': 'staged',
        'live': true,
      });
    },
    saveChanges({ commit, rootState, dispatch, state }: any, obj: any) {
      let dateNow = new Date()
      let form = obj.form
      firebase.database().ref(`/integration_settings/${rootState.authen.user.company}`).update({
        token: form.token_temp ? form.token_temp : form.token,
        sync_type: form.sync_type,
        live: form.live,
        abbreviate_team_name: form.abbreviate_team_name,
        updated: format(dateNow, 'YYYY-MM-DD HH:mm')
      }).then(() => {
        dispatch('authen/addLogs', {
          company: rootState.authen.user.company,
          createdate: format(dateNow, 'YYYY-MM-DD HH:mm:ss'),
          id: rootState.authen.user.company,
          table: 'integration_settings',
          status: 'updateIntegrationSetting',
          user: rootState.authen.user.email
        }, { root: true })
      }).then(() => {
        obj.messageLoading.close();
        obj.thisvue.$message({
          type: 'success',
          message: 'Your settings have been updated',
        });
      })
    },
    async getSimulateData({ commit, rootState, dispatch, state }: any, obj: any) {
      commit('setLoadingSimulateTable', true)
      let allUser: Array<any> = [];
      let arrUser: Array<any> = [];
      state.membersSimulate = []
      state.membersSimulate = []
      let start_date = format(parse(obj.batch_ct), 'MMM d, YYYY')
      let start_date_format = format(parse(obj.batch_ct), 'YYYY-MM-DD')
      const allUserKey = await getUserAll(obj.comp_id)//rootState.main.allUser //(await firebase.database().ref('users').orderByChild("company").equalTo(obj.comp_id).once('value')).val()
      const team = await getTeamByCompany(obj.comp_id)//(await firebase.database().ref('team').orderByChild("company").equalTo(obj.comp_id).once('value')).val()
      for(const arrData in allUserKey){
        const u =  allUserKey[arrData]
        u.key = arrData
        u.value = allUserKey[arrData].firstname + " " + allUserKey[arrData].lastname
        let k_boss = allUserKey[arrData].boss.replace(/\./g, "||")
        if (k_boss && allUserKey[k_boss] != undefined) {
          u.boss_name = allUserKey[k_boss]["firstname"] + " " + allUserKey[k_boss]["lastname"]
        }
        if (u.team) u.team_name = (team[u.team] != undefined) ? team[u.team]["name"] : ''
        if (u.profilepic_small) {
          u.pic = u.profilepic_small;
        } else if (u.profilepic_crop) {
          u.pic = u.profilepic_crop;
        } else if (u.profilepic) {
          u.pic = u.profilepic;
        } else {
          u.pic = "";
        }
        u.change_type = 'None'
        allUser[u.key] = u
      }
      await firebase.database().ref().child('users_preview').orderByChild('batch_ct').equalTo(`${obj.batch_ct}`).once('value', snapshot => {
        snapshot.forEach((arrData): any => {
          const u = arrData.val()
          if (u.company == obj.comp_id) {
            u.key = arrData.key
            u.value = arrData.val().firstname + " " + arrData.val().lastname
            let k_boss = arrData.val().boss.replace(/\./g, "||")
            if (k_boss && allUserKey[k_boss] != undefined) {
              u.boss_name = allUserKey[k_boss]["firstname"] + " " + allUserKey[k_boss]["lastname"]
            }
            u.team_name = (u.team != undefined) ? u.team : ''
            u.pic = (allUser[u.key] != undefined) ? allUser[u.key].pic : ''
            if (u.action_type == 'add') {
              u.change_type = 'New Member'
            } else if (u.action_type == 'update') {
              u.change_type = 'Profile Updated'
            } else if (u.action_type == 'delete') {
              u.change_type = 'Archived'
            }
            u.start_date = start_date
            u.start_date_format = start_date_format
            allUser[u.key] = u
          }
        });
        for (const x in allUser) {
          arrUser.push(allUser[x])
        }
        var _users = _.sortBy(arrUser, [function (o: any) { return o.firstname; }])
        commit('setResultsSimulate', arrUser)
        commit('setMemberSimulate', _.sortBy(_users.slice(0, state.size_page), [function (o: any) { return o.firstname; }]))
        commit('setMemberSimulateAll', _.sortBy(arrUser, [function (o: any) { return o.firstname; }]))
        commit('setLoadingSimulateTable', false)
        if (state.inputSearchSimulate) {
          dispatch('searchMembersSimulate', state.inputSearchSimulate)
        }
      });
      // await firebase.database().ref().child('users').orderByChild('company').equalTo(`${obj.comp_id}`).once('value', async snapshot => {
      //   snapshot.forEach((arrData): any => {
      //     const u = arrData.val()
      //     u.key = arrData.key
      //     u.value = arrData.val().firstname + " " + arrData.val().lastname
      //     let k_boss = arrData.val().boss.replace(/\./g, "||")
      //     if (k_boss && allUserKey[k_boss] != undefined) {
      //       u.boss_name = allUserKey[k_boss]["firstname"] + " " + allUserKey[k_boss]["lastname"]
      //     }
      //     if (u.team) u.team_name = (team[u.team] != undefined) ? team[u.team]["name"] : ''
      //     if (u.profilepic_small) {
      //       u.pic = u.profilepic_small;
      //     } else if (u.profilepic_crop) {
      //       u.pic = u.profilepic_crop;
      //     } else if (u.profilepic) {
      //       u.pic = u.profilepic;
      //     } else {
      //       u.pic = "";
      //     }
      //     u.change_type = 'None'
      //     allUser[u.key] = u
      //   });
      //   await firebase.database().ref().child('users_preview').orderByChild('batch_ct').equalTo(`${obj.batch_ct}`).once('value', snapshot => {
      //     snapshot.forEach((arrData): any => {
      //       const u = arrData.val()
      //       if (u.company == obj.comp_id) {
      //         u.key = arrData.key
      //         u.value = arrData.val().firstname + " " + arrData.val().lastname
      //         let k_boss = arrData.val().boss.replace(/\./g, "||")
      //         if (k_boss && allUserKey[k_boss] != undefined) {
      //           u.boss_name = allUserKey[k_boss]["firstname"] + " " + allUserKey[k_boss]["lastname"]
      //         }
      //         u.team_name = (u.team != undefined) ? u.team : ''
      //         u.pic = (allUser[u.key] != undefined) ? allUser[u.key].pic : ''
      //         if (u.action_type == 'add') {
      //           u.change_type = 'New Member'
      //         } else if (u.action_type == 'update') {
      //           u.change_type = 'Profile Updated'
      //         } else if (u.action_type == 'delete') {
      //           u.change_type = 'Archived'
      //         }
      //         u.start_date = start_date
      //         u.start_date_format = start_date_format
      //         allUser[u.key] = u
      //       }
      //     });
      //     for (const x in allUser) {
      //       arrUser.push(allUser[x])
      //     }
      //     var _users = _.sortBy(arrUser, [function (o: any) { return o.firstname; }])
      //     commit('setResultsSimulate', arrUser)
      //     commit('setMemberSimulate', _.sortBy(_users.slice(0, state.size_page), [function (o: any) { return o.firstname; }]))
      //     commit('setMemberSimulateAll', _.sortBy(arrUser, [function (o: any) { return o.firstname; }]))
      //     commit('setLoadingSimulateTable', false)
      //     if (state.inputSearchSimulate) {
      //       dispatch('searchMembersSimulate', state.inputSearchSimulate)
      //     }
      //   });
      // }).catch(error => {
      //   console.log(error)
      // });
    },
    searchMembersSimulate({ commit, state }: any, value: string) {
      let field = state.sortfieldsimulate
      let order = state.sortordersimulate
      commit('setPageSimulate', 1)
      const member: Array<any> = [];
      member.push(state.membersSimulateAll)

      if (field == 'name')
        field = 'value'

      if (order == 'ascending' || order == 'asc')
        order = 'asc'
      else
        order = 'desc'

      if (value.length > 2) {
        if (member.length > 0) {
          var re = new RegExp(value, 'gi')
          var response = _.filter(member[0], (item): any => {
            if (item.firstname.match(re)) {
              return true
            } else if (item.lastname != undefined && item.lastname.match(re)) {
              return true
            } else if (item.email != undefined && item.email.match(re)) {
              return true
            } else if (item.team_name != undefined && item.team_name.match(re)) {
              return true
            }
          });
          let response_sort = _.orderBy(response, [field], [order]);
          commit('setResultsSimulate', response_sort)
          commit('setMemberSimulate', response_sort.slice(0, state.size_page))
        }
      } else if (value.length == 0) {
        let response_sort = _.orderBy(member[0], [field], [order]);
        commit('setResultsSimulate', response_sort)
        commit('setMemberSimulate', response_sort.slice(0, state.size_page))
      }
    },
    sortMemberSimulate({ commit, state }: any, value: any) {
      var member: Array<any> = [];
      member.push(state.membersSimulateAll)
      if (state.inputSearchSimulate.length > 2) {
        if (member.length > 0) {
          var re = new RegExp(state.inputSearchSimulate, 'gi')
          var response = _.filter(member[0], (item): any => {
            if (item.firstname.match(re)) {
              return true
            } else if (item.lastname != undefined && item.lastname.match(re)) {
              return true
            } else if (item.email != undefined && item.email.match(re)) {
              return true
            } else if (item.team_name != undefined && item.team_name.match(re)) {
              return true
            }
          });
          member = []
          member.push(response)
        }
      }

      var size = state.size_page
      var page = state.pagesimulate
      var field = value[0]
      var order = value[1]
      var start_row = (page - 1) * size
      var end_row = ((page - 1) * size) + size

      if (field == 'name')
        field = 'value'

      if (order == 'ascending' || order == 'asc') {
        order = 'asc'
      } else {
        order = 'desc'
      }
      var _users = _.orderBy(member[0], [field], [order]);
      commit('setSortfieldSimulate', field)
      commit('setSortorderSimulate', order)
      commit('setMemberSimulate', _users.slice(start_row, end_row))
    },
    simulateCurrentChange({ commit, state }: any, page: any) {
      var size = state.size_page
      var start_row = (page - 1) * size
      var end_row = ((page - 1) * size) + size
      let member: Array<any> = [];
      member.push(state.membersSimulateAll)
      if (state.inputSearchSimulate.length > 2) {
        if (member.length > 0) {
          var re = new RegExp(state.inputSearchSimulate, 'gi')
          var response = _.filter(member[0], (item): any => {
            if (item.firstname.match(re)) {
              return true
            } else if (item.lastname != undefined && item.lastname.match(re)) {
              return true
            } else if (item.email != undefined && item.email.match(re)) {
              return true
            } else if (item.team_name != undefined && item.team_name.match(re)) {
              return true
            }
          });
          member[0] = response
        }
      }

      var field = state.sortfieldsimulate
      var _users = _.orderBy(member[0], [field], [state.sortordersimulate]);
      commit('setPageSimulate', page)
      commit('setMemberSimulate', _users.slice(start_row, end_row))
    },
    async getViewData({ commit, rootState, dispatch, state }: any, obj: any) {
      commit('setLoadingViewTable', true)
      let allUser: Array<any> = [];
      let arrUser: Array<any> = [];
      state.membersView = []
      state.membersView = []
      let start_date = format(parse(obj.batch_ct), 'MMM d, YYYY')
      let start_date_format = format(parse(obj.batch_ct), 'YYYY-MM-DD')
      const allUserKey = await getUserAll(obj.comp_id)//rootState.main.allUser//(await firebase.database().ref('users').orderByChild("company").equalTo(obj.comp_id).once('value')).val()
      const team =await getTeamByCompany(obj.comp_id)// (await firebase.database().ref('team').orderByChild("company").equalTo(obj.comp_id).once('value')).val()
      await firebase.database().ref().child('integration_changelogs').orderByChild('batch_ct').equalTo(`${obj.batch_ct}`).once('value', snapshot => {
        snapshot.forEach((arrData): any => {
          const u = arrData.val()
          if (u.comp_id == obj.comp_id) {
            if (u.change_logs.length > 0) {
              _.forEach(u.change_logs, (item, key) => {
                let dataUser = item
                dataUser.key = dataUser.email.replace(/\./g, "||")
                dataUser.value = dataUser.firstname + " " + dataUser.lastname
                let k_boss = dataUser.boss.replace(/\./g, "||")
                if (k_boss && allUserKey[k_boss] != undefined) {
                  dataUser.boss_name = allUserKey[k_boss]["firstname"] + " " + allUserKey[k_boss]["lastname"]
                }
                dataUser.team_name = (dataUser.team != undefined) ? dataUser.team : ''
                if (allUserKey[dataUser.key] != undefined) {
                  if (allUserKey[dataUser.key].profilepic_small) {
                    dataUser.pic = allUserKey[dataUser.key].profilepic_small;
                  } else if (allUserKey[dataUser.key].profilepic_crop) {
                    dataUser.pic = allUserKey[dataUser.key].profilepic_crop;
                  } else if (allUserKey[dataUser.key].profilepic) {
                    dataUser.pic = allUserKey[dataUser.key].profilepic;
                  } else {
                    dataUser.pic = "";
                  }
                } else {
                  dataUser.pic = "";
                }

                if (dataUser.action_type == 'add') {
                  dataUser.change_type = 'New Member'
                } else if (dataUser.action_type == 'update') {
                  dataUser.change_type = 'Profile Updated'
                } else if (dataUser.action_type == 'delete') {
                  dataUser.change_type = 'Archived'
                }
                dataUser.start_date = start_date
                dataUser.start_date_format = start_date_format
                arrUser.push(dataUser)
              });
            }
          }
        });
        var _users = _.sortBy(arrUser, [function (o: any) { return o.firstname; }])
        commit('setResultsView', arrUser)
        commit('setMemberView', _.sortBy(_users.slice(0, state.size_page), [function (o: any) { return o.firstname; }]))
        commit('setMemberViewAll', _.sortBy(arrUser, [function (o: any) { return o.firstname; }]))
        commit('setLoadingViewTable', false)
        if (state.inputSearchView) {
          dispatch('searchMembersView', state.inputSearchview)
        }
      });
    },
    searchMembersView({ commit, state }: any, value: string) {
      let field = state.sortfieldview
      let order = state.sortorderview
      commit('setPageView', 1)
      const member: Array<any> = [];
      member.push(state.membersViewAll)

      if (field == 'name')
        field = 'value'

      if (order == 'ascending' || order == 'asc')
        order = 'asc'
      else
        order = 'desc'

      if (value.length > 2) {
        if (member.length > 0) {
          var re = new RegExp(value, 'gi')
          var response = _.filter(member[0], (item): any => {
            if (item.firstname.match(re)) {
              return true
            } else if (item.lastname != undefined && item.lastname.match(re)) {
              return true
            } else if (item.email != undefined && item.email.match(re)) {
              return true
            } else if (item.team_name != undefined && item.team_name.match(re)) {
              return true
            }
          });
          let response_sort = _.orderBy(response, [field], [order]);
          commit('setResultsView', response_sort)
          commit('setMemberView', response_sort.slice(0, state.size_page))
        }
      } else if (value.length == 0) {
        let response_sort = _.orderBy(member[0], [field], [order]);
        commit('setResultsView', response_sort)
        commit('setMemberView', response_sort.slice(0, state.size_page))
      }
    },
    sortMemberView({ commit, state }: any, value: any) {
      var member: Array<any> = [];
      member.push(state.membersViewAll)
      if (state.inputSearchView.length > 2) {
        if (member.length > 0) {
          var re = new RegExp(state.inputSearchView, 'gi')
          var response = _.filter(member[0], (item): any => {
            if (item.firstname.match(re)) {
              return true
            } else if (item.lastname != undefined && item.lastname.match(re)) {
              return true
            } else if (item.email != undefined && item.email.match(re)) {
              return true
            } else if (item.team_name != undefined && item.team_name.match(re)) {
              return true
            }
          });
          member = []
          member.push(response)
        }
      }

      var size = state.size_page
      var page = state.pageview
      var field = value[0]
      var order = value[1]
      var start_row = (page - 1) * size
      var end_row = ((page - 1) * size) + size

      if (field == 'name')
        field = 'value'

      if (order == 'ascending' || order == 'asc') {
        order = 'asc'
      } else {
        order = 'desc'
      }
      var _users = _.orderBy(member[0], [field], [order]);
      commit('setSortfieldView', field)
      commit('setSortorderView', order)
      commit('setMemberView', _users.slice(start_row, end_row))
    },
    viewCurrentChange({ commit, state }: any, page: any) {
      var size = state.size_page
      var start_row = (page - 1) * size
      var end_row = ((page - 1) * size) + size
      let member: Array<any> = [];
      member.push(state.membersViewAll)
      if (state.inputSearchView.length > 2) {
        if (member.length > 0) {
          var re = new RegExp(state.inputSearchView, 'gi')
          var response = _.filter(member[0], (item): any => {
            if (item.firstname.match(re)) {
              return true
            } else if (item.lastname != undefined && item.lastname.match(re)) {
              return true
            } else if (item.email != undefined && item.email.match(re)) {
              return true
            } else if (item.team_name != undefined && item.team_name.match(re)) {
              return true
            }
          });
          member[0] = response
        }
      }

      var field = state.sortfieldview
      var _users = _.orderBy(member[0], [field], [state.sortorderview]);
      commit('setPageView', page)
      commit('setMemberView', _users.slice(start_row, end_row))
    },

    async getReviewUpdatesTotal({commit,rootState,dispatch,state}:any, user:any){
      let query = firebase.firestore()
        .collection('integrations')
        .where('company','==', user.company);
      
      if (!state.reviewUpdatesIgnorePendingStatus) {
        query = query.where('status','==', 'pending')
      }

      await query.get()
        .then((querySnapshot)=> {
          commit('setReviewUpdatesTotal', querySnapshot.size);
        });
    },

    beginBulkUpdates({ commit, rootState, dispatch, state }: any, keys: string[]) {
      commit("setReviewUpdatesLoading", true);
      state._reviewUpdates = state.reviewUpdates;
      state._reviewUpdatesAll = state.reviewUpdatesAll;
      commit("setReviewUpdates", []);
      commit("setReviewUpdatesAll", []);
    },

    endBulkUpdates({ commit, rootState, dispatch, state }: any, keys: []) {
      commit("setReviewUpdatesLoading", false);

      const reviewUpdates = _.cloneDeep(state._reviewUpdates);
      const reviewUpdatesAll = _.cloneDeep(state._reviewUpdatesAll);

      for (const i in keys) {
        _.remove(reviewUpdates, {key: keys[i]});
        _.remove(reviewUpdatesAll, {key: keys[i]});
      }

      commit("setReviewUpdates", reviewUpdates);
      commit("setReviewUpdatesAll", reviewUpdatesAll);

      // @todo pagination
      // const items = _.cloneDeep(state.reviewUpdates);
      const items = state.reviewUpdates;
      const size = state.size_page
      const page = state.pageReviewUpdates
      const start_row = (page-1) * size
      const end_row = ((page-1) * size)+size

      const pageTotal = reviewUpdatesAll.length;

      const newPage = Math.ceil((pageTotal -1) / size)

      // update current items in table view
      const realItems = reviewUpdatesAll.slice(start_row,end_row)
      if (realItems.length > items.length) {
        items.push(realItems[items.length -1]);
      }

      // update pagination
      // state.reviewUpdates = items;
      state.reviewUpdates = _.clone(items); // prevent non-update on view
      if (newPage < page) {
        commit('setPageReviewUpdates', newPage)
      }

      // update page total
      commit('setReviewUpdatesTotal', pageTotal);
    },

    async updateReviewUpdates({commit,rootState,dispatch,state}:any, item:any){
      // console.log('updateReviewUpdates()');
      // console.log(item, "item");

      // firebase.firestore().collection('townhall').doc(topic_id).update(topic).then(() => {
      await firebase.firestore().collection(`integrations`).doc(item.key).get().then(async (doc) => {    
        await firebase.firestore().collection('integrations').doc(item.key).update({ status: item.status }).then(() => {
          // console.log('remove item: ' + item.key);

          /*

          _.remove(state.reviewUpdates, {key: item.key});
          _.remove(state.reviewUpdatesAll, {key: item.key});

          // const items = _.cloneDeep(state.reviewUpdates);
          const items = state.reviewUpdates;
          const size = state.size_page
          const page = state.pageReviewUpdates
          const start_row = (page-1) * size
          const end_row = ((page-1) * size)+size

          const pageTotal = state.reviewUpdatesAll.length;

          const newPage = Math.ceil((pageTotal -1) / size)

          // update current items in table view
          const realItems = state.reviewUpdatesAll.slice(start_row,end_row)
          if (realItems.length > items.length) {
            items.push(realItems[items.length -1]);
          }

          // update pagination
          // state.reviewUpdates = items;
          state.reviewUpdates = _.clone(items); // prevent non-update on view
          if (newPage < page) {
            commit('setPageReviewUpdates', newPage)
          }

          // update page total
          commit('setReviewUpdatesTotal', pageTotal);

          //*/
        });
      });
    },
    async getReviewUpdates({commit,rootState,dispatch,state}:any, user:any){
      commit('setReviewUpdatesLoading', true)
      state.reviewUpdates = []
      state.reviewUpdatesAll = []

      let items: Array<any> = [];
      const reviewUpdatesByKey :Array<any> = [];

      // @todo use from another
      const company = await getCompany(user.company)//(await firebase.database().ref(`company/${user.company}`).once('value')).val();
      const allUser =await getUserAll(user.company)//(await firebase.database().ref('users').orderByChild("company").equalTo(user.company).once('value')).val();
      // console.log(allUser, "allUser");
      const teamByKey = rootState.newmember.teamByKey;

      // const $utils = Vue.prototype.$utilities;

      let query = firebase.firestore()
        .collection('integrations')
        .where('company','==', user.company);

      if (!state.reviewUpdatesIgnorePendingStatus) {
        query = query.where('status','==', 'pending')
      }

      await query
        // .orderBy('createdate', 'desc') // affected to pagination when press next page
        .get()
        .then((querySnapshot)=> {
            querySnapshot.forEach((doc)=> {
              // console.log(doc.id, " => ", doc.data());
              let data = doc.data();

              // @todo fields: pic, firstname, lastname
              const email =  _.get(data, "email", "");
              const emailkey = email.replace(/\./g, "||");

              const applyUserInfo = (email) => {
                const emailkey = email.replace(/\./g, "||");
                data.firstname = _.get(allUser, `${emailkey}.firstname`, "");
                data.lastname = _.get(allUser, `${emailkey}.lastname`, "");
                data.name = `${data.firstname} ${data.lastname} ${email}`;
              }

              const applyRequestedBy = (email) => {
                if (!email) return;
                const emailkey = email.replace(/\./g, "||");
                const firstname = _.get(allUser, `${emailkey}.firstname`, "");
                const lastname = _.get(allUser, `${emailkey}.lastname`, "");
                data.requested_by = `${firstname} ${lastname.length ? lastname[0] : ""}`;
              }

              if (!_.get(data, "firstname") || !_.get(data, "lastname")) {
                applyUserInfo(email);
              }

              data.key = doc.id;

              // important data for edit in table
              data.name = `${data.firstname} ${data.lastname} ${data.email}`;
              data.manager = "";
              data.country = _.get(company, "country", "TH");
              data.language = data.country == "TH" ? "th" : "en";
              data.archived_type = "";

              // for read only input
              if (data.source != "hris") {
                data.manager_name = "";
                data.team_name = "";
              }

              let managerOption = null;
              if (data.source == "app") {
                // @fixed data mapping and getting requirements
                if (data.type == "direct_manager") {
                  const _email = _.get(data, "email", "");
                  const _email_request = _.get(data, "email_request", "");
                  data.email = _email_request;
                  data.manager = _email;

                  const bossKey = data.manager.replace(/\./g, "||");
                  const bossInfo = _.get(allUser, `${bossKey}`, {});
                  data.team = _.get(bossInfo, "team", "");

                  applyUserInfo(data.email);
                  applyRequestedBy(_email_request);
                } else if (data.type == "re_assign") {
                  const _email_request = _.get(data, "email_request", "");
                  data.team = "";
                  applyUserInfo(data.email);
                  applyRequestedBy(_email_request);
                } else if (data.type == "add_member") {
                  const _email_request = _.get(data, "email_request", "");
                  data.manager = _email_request;
                  applyRequestedBy(_email_request);
                } else if (data.type == "remove") {
                  applyUserInfo(data.email);
                  applyRequestedBy(data.email_request);

                  // team_name, manager_name, requested_by
                  const userInfo = _.get(allUser, emailkey, {});
                  data.team = _.get(userInfo, "team", "");
                  data.manager = _.get(userInfo, "boss", "");
                }

              } else if (data.source == "hris") {
                if (_.has(data, "direct_manager_email")) {
                  data.manager = _.get(data, "direct_manager_email", "");
                }
                if (_.has(data, "team")) {
                  data.team = _.get(data, "team", "");
                }
                if (data.type == "delete") {
                  data.manager = _.get(allUser, `${emailkey}.boss`, "");
                  data.team = _.get(allUser, `${emailkey}.team`, "");
                } else if (data.type == "create") {
                  data.manager = _.get(data, `manager_email`, "");
                  data.team_name = _.get(data, `team_name`, "");
                  if (data.team_name) {
                    data.team = _.findKey(teamByKey, {name: data.team_name});
                  }
                }
              }

              if (!_.has(data, "team")) {
                data.team = "";
              }

              if (data.manager) {
                managerOption = _.find(allUser, {email: data.manager});
                data.manager_name = managerOption ? 
                  `${_.get(managerOption, "firstname", "")} ${_.get(managerOption, "lastname", "")}`: 
                  data.manager;
              }

              if (data.team) {
                data.team_name = _.has(teamByKey, data.team) ?
                  _.get(teamByKey, `${data.team}.name`, "") :
                  data.team;
              }

              // resolve default manager label
              /*
              if (data.manager) {
                const managerIndex = _.findIndex(managerOptions, function(o) { return o.email == data.manager; });
                // console.log(data.manager, managerIndex);
                data.manager = managerIndex;
              }
              //*/
              

              items.push(data);
              reviewUpdatesByKey[doc.id] = data;
            })
          });

      commit('setReviewUpdatesByKey', reviewUpdatesByKey);
      commit('setReviewUpdatesAll', _.clone(items));
      commit('setReviewUpdatesTotal', items.length);
      items = _.sortBy(items.slice(0, state.size_page), [function(o:any) { return o.email; }]);

      commit('setPageReviewUpdates', 1);
      commit('setReviewUpdates', items);

      commit('setReviewUpdatesLoading', false);
    },
    sortReviewUpdates({commit,state}:any,value:any){
      //*
      var member: Array<any> = [];
      member.push(state.reviewUpdatesAll)
      /*
      if (state.inputSearchArchive.length > 2){
        if (member.length > 0){
          var re = new RegExp(state.inputSearchArchive, 'gi')
          var response = _.filter(member[0], (item): any => {
            if (item.firstname.match(re)) {
              return true
            } else if (item.lastname != undefined && item.lastname.match(re)) {
              return true
            } else if (item.value != undefined && (item.value.match(re))) {
              return true
            } else if (item.email != undefined && item.email.match(re)) {
              return true
            } else if (item.team_name != undefined && item.team_name.match(re)) {
              return true
            } else if (item.boss_firstname != undefined && item.boss_firstname.match(re)) {
              return true
            } else if (item.boss_lastname != undefined && item.boss_lastname.match(re)) {
              return true
            } else if (item.boss_name != undefined && item.boss_name.match(re)) {
              return true
            }
          });
          member = []
          member.push(response)
        }
      }
      //*/

      var size = state.size_page
      var page = state.pageReviewUpdates
      var field  = value[0]
      var order = value[1]
      var start_row = (page-1) * size
      var end_row = ((page-1) * size)+size

      // if(field == 'name')
      //   field = 'value'

      if(order == 'ascending' || order == 'asc'){
        if(field == 'exitdated'){
          order = 'desc'
        }else {
          order = 'asc'
        }
      }else {
        if(field == 'exitdated'){
          order = 'asc'
        }else{
          order = 'desc'
        }
      }
      var _users = _.orderBy(member[0], [field], [order]);
      commit('setSortfieldReviewUpdates', field)
      commit('setSortorderReviewUpdates', order)
      commit('setReviewUpdates', _users.slice(start_row,end_row))
      //*/
    },
    reviewUpdatesCurrentChange({commit,state}:any,page:any){
      var size = state.size_page
      var start_row = (page-1) * size
      var end_row = ((page-1) * size)+size
      let member: Array<any> = [];
      member.push(state.reviewUpdatesAll)
      // if (state.inputSearchArchive.length > 2){
      //   if (member.length > 0){
      //     var re = new RegExp(state.inputSearchArchive, 'gi')
      //     var response = _.filter(member[0], (item): any => {
      //       if (item.firstname.match(re)) {
      //         return true
      //       } else if (item.lastname != undefined && item.lastname.match(re)) {
      //         return true
      //       } else if (item.value != undefined && (item.value.match(re))) {
      //         return true
      //       } else if (item.email != undefined && item.email.match(re)) {
      //         return true
      //       } else if (item.team_name != undefined && item.team_name.match(re)) {
      //         return true
      //       } else if (item.boss_firstname != undefined && item.boss_firstname.match(re)) {
      //         return true
      //       } else if (item.boss_lastname != undefined && item.boss_lastname.match(re)) {
      //         return true
      //       } else if (item.boss_name != undefined && item.boss_name.match(re)) {
      //         return true
      //       }
      //     });
      //     member[0] = response
      //   }
      // }
      
      var field = state.sortfieldreviewupdates
      var _users = _.orderBy(member[0], [field], [state.sortorderreviewupdates]);
      commit('setPageReviewUpdates', page)
      commit('setReviewUpdates', _users.slice(start_row, end_row))
    },

    async validateNewMembersWithUpdate({commit,rootState}:any, data:any){
      const rows = _.cloneDeep(Array.isArray(data) ? data : [])
      const header = rows[0]
      if (!Array.isArray(header)) {
        commit('setImportMembersTableDataGroupsTotal', 0)
        commit('setImportMembersTableDataGroups', [])
        return
      }

      const user = rootState.authen.user
      const company = await importMemberDeps.getCompany(user.company)
      const companyCountry = company.country || 'TH'
      const defaultLanguage = companyCountry === 'TH' ? 'th' : 'en'
      const defaultTimezone = normalizeImportTimezone(company.timezone || 7, 7).value
      const existingMembers = _.values(_.get(rootState, 'main.allUser', []))
      const teamByKey = _.get(rootState, 'newmember.teamByKey', {})
      const locationOptions = _.get(rootState, 'newmember.locationOption', [])
      const archivedMembersByKey = await importMemberDeps.getArchivedMembers(user.company)
      const teamImportAliases = await importMemberDeps.getTeamImportAliases(user.company)
      const preview = buildImportPreview({
        rows,
        existingMembers,
        teamByKey,
        defaults: {
          companyCountry,
          defaultLanguage,
          defaultTimezone,
          locationOptions,
        },
        archivedMembersByKey,
        teamImportAliases,
      })

      commit('setImportMembersTableDataGroupsTotal', preview.total)
      commit('setImportMembersTableDataGroups', preview.dataGroups)
    },
    async revalidateImportMembersReview({commit, state, rootState}: any) {
      const reviewItems = _.flatMap(
        (state.importMembersTableDataGroups || []).filter((group: any) => ['new', 'update', 'resolve'].indexOf(group.code) !== -1),
        'items'
      )

      if (reviewItems.length === 0) {
        commit('setImportMembersTableDataGroupsTotal', 0)
        commit('setImportMembersTableDataGroups', [])
        return
      }

      const user = rootState.authen.user
      const company = await importMemberDeps.getCompany(user.company)
      const companyCountry = company.country || 'TH'
      const defaultLanguage = companyCountry === 'TH' ? 'th' : 'en'
      const defaultTimezone = normalizeImportTimezone(company.timezone || 7, 7).value
      const existingMembers = _.values(_.get(rootState, 'main.allUser', []))
      const teamByKey = _.get(rootState, 'newmember.teamByKey', {})
      const locationOptions = _.get(rootState, 'newmember.locationOption', [])
      const archivedMembersByKey = await importMemberDeps.getArchivedMembers(user.company)
      const teamImportAliases = await importMemberDeps.getTeamImportAliases(user.company)
      const evaluation = buildImportEvaluationFromReviewItems({
        items: reviewItems,
        existingMembers,
        teamByKey,
        defaults: {
          companyCountry,
          defaultLanguage,
          defaultTimezone,
          locationOptions,
        },
        archivedMembersByKey,
        teamImportAliases,
      })

      commit('setImportMembersTableDataGroupsTotal', evaluation.total)
      commit('setImportMembersTableDataGroups', evaluation.dataGroups)
    },
    async importMembersResolve({commit, dispatch, state,rootState}:any){
      const unresolvedGroup = _.find(state.importMembersTableDataGroups, { code: 'resolve' })
      if (unresolvedGroup && unresolvedGroup.items.length > 0) {
        throw new Error('Resolve all import issues before confirming the import.')
      }

      const reviewedItems = _.flatMap(
        state.importMembersTableDataGroups.filter((group: any) => ['new', 'update'].indexOf(group.code) !== -1),
        'items'
      )

      if (reviewedItems.length === 0) {
        return {
          backupId: null,
          importedCount: 0,
        }
      }

      const companyId = rootState.authen.user.company
      const actorEmail = rootState.authen.user.email
      const company = await importMemberDeps.getCompany(companyId)
      const companyCountry = company.country || 'TH'
      const defaultLanguage = companyCountry === 'TH' ? 'th' : 'en'
      const defaultTimezone = normalizeImportTimezone(company.timezone || 7, 7).value
      const locationOptions = _.get(rootState, 'newmember.locationOption', [])
      const teamMaster = _.cloneDeep(await importMemberDeps.getTeamByCompany(companyId) || {})
      const allUser = _.cloneDeep(await importMemberDeps.getUserAll(companyId) || {})
      const archivedMembersByKey = await importMemberDeps.getArchivedMembers(companyId)
      const teamImportAliases = await importMemberDeps.getTeamImportAliases(companyId)
      const evaluation = buildImportEvaluationFromReviewItems({
        items: reviewedItems,
        existingMembers: _.values(allUser),
        teamByKey: teamMaster,
        defaults: {
          companyCountry,
          defaultLanguage,
          defaultTimezone,
          locationOptions,
        },
        archivedMembersByKey,
        teamImportAliases,
      })

      commit('setImportMembersTableDataGroupsTotal', evaluation.total)
      commit('setImportMembersTableDataGroups', evaluation.dataGroups)

      const freshUnresolvedGroup = _.find(evaluation.dataGroups, { code: 'resolve' })
      if (freshUnresolvedGroup && freshUnresolvedGroup.items.length > 0) {
        throw new Error('Import data changed while you were reviewing it. Resolve the new issues before confirming the import.')
      }

      const actionableItems = _.flatMap(
        evaluation.dataGroups.filter((group: any) => ['new', 'update'].indexOf(group.code) !== -1),
        'items'
      )

      if (actionableItems.length === 0) {
        return {
          backupId: null,
          importedCount: 0,
        }
      }

      const nowTimestamp = format(new Date(), 'YYYY-MM-DD HH:mm:ss')
      const backupId = importMemberDeps.createDatabaseKey(`company/${companyId}/import_member_backups`)
      const backupPath = `company/${companyId}/import_member_backups/${backupId}`
      const getImportErrorMessage = (error: any) => {
        return normalizeImportString(error && error.message ? error.message : error)
      }
      const isPermissionDeniedError = (error: any) => {
        const message = getImportErrorMessage(error).toLowerCase()
        return message.indexOf('permission denied') !== -1 || message.indexOf('permission_denied') !== -1
      }

      const impactedUserKeys = _.uniq(
        actionableItems
          .map((item: any) => normalizeImportEmail(item.email))
          .filter(Boolean)
          .map((email: string) => email.replace(/\./g, '||'))
      )
      const usersBefore: Record<string, any> = {}
      impactedUserKeys.forEach((key) => {
        usersBefore[key] = _.has(allUser, key) ? _.cloneDeep(allUser[key]) : null
      })

      try {
        await importMemberDeps.setDatabaseValue(backupPath, {
          company: companyId,
          created_at: nowTimestamp,
          created_by: actorEmail,
          status: 'pending',
          items: _.cloneDeep(actionableItems),
          users_before: usersBefore,
          teams_before: _.cloneDeep(teamMaster),
        })
      } catch (error) {
        if (isPermissionDeniedError(error)) {
          throw new Error(`Permission denied while saving the pre-import backup at ${backupPath}.`)
        }
        throw error
      }

      const nowDate = format(new Date(), 'YYYY-MM-DD HH:mm')
      try {
        const mutationPlan = buildImportMutationPlan({
          actionableItems,
          allUser,
          teamByKey: teamMaster,
          companyId,
          nowDate,
          nowTimestamp,
          actorEmail,
          idFactory: {
            nextTeamId: () => importMemberDeps.createDatabaseKey('team'),
            nextGenBossId: () => importMemberDeps.createDatabaseKey('genboss'),
            nextGenOrganizeId: () => importMemberDeps.createDatabaseKey('genorganize'),
          },
        })

        await importMemberDeps.batchDatabaseUpdate(mutationPlan.updates)
        importMemberDeps.invalidateMemberAndTeamCaches(companyId)
        try {
          await dispatch('main/refreshTeamsAndHeight', null, { root: true })
        } catch (refreshError) {
          console.warn('refreshTeamsAndHeight after member import failed', refreshError)
        }

        let logError = ''
        try {
          // Resolve the caller IP once for the whole import instead of letting
          // each addLogs call make its own ipify round-trip (a 500-row import
          // otherwise fires ~500 serial IP lookups).
          const callerIp = await importMemberDeps.resolveCallerIp()
          const logErrors: string[] = []
          // Write audit entries concurrently in bounded batches, and keep going
          // past individual failures so one bad write can't truncate the trail.
          const LOG_BATCH_SIZE = 20
          for (let i = 0; i < mutationPlan.logs.length; i += LOG_BATCH_SIZE) {
            const batch = mutationPlan.logs.slice(i, i + LOG_BATCH_SIZE)
            await Promise.all(batch.map(async (log) => {
              const logPayload: any = {
                company: companyId,
                createdate: format(new Date(), 'YYYY-MM-DD HH:mm:ss'),
                id: log.id,
                table: log.table,
                status: log.status,
                user: actorEmail,
                ip: callerIp,
              }
              if (log.source) {
                logPayload.source = log.source
              }
              try {
                await importMemberDeps.addLogs(dispatch, logPayload)
              } catch (error) {
                logErrors.push(getImportErrorMessage(error))
              }
            }))
          }
          if (logErrors.length) {
            logError = `${logErrors.length} of ${mutationPlan.logs.length} audit log entries failed: ${_.uniq(logErrors).join('; ')}`
          }
        } catch (error) {
          logError = getImportErrorMessage(error)
        }

        try {
          await importMemberDeps.updateDatabaseValue(backupPath, {
            status: 'completed',
            completed_at: format(new Date(), 'YYYY-MM-DD HH:mm:ss'),
            created_users: mutationPlan.createdUsers,
            updated_users: mutationPlan.updatedUsers,
            created_teams: mutationPlan.createdTeams,
            touched_team_ids: mutationPlan.touchedTeamIds,
            saved_team_aliases: mutationPlan.savedTeamAliases,
            log_error: logError,
          })
        } catch (backupError) {
          // The import itself succeeded, so preserve success even if backup bookkeeping fails.
        }

        return {
          backupId,
          importedCount: actionableItems.length,
        }
      } catch (error) {
        try {
          await importMemberDeps.updateDatabaseValue(backupPath, {
            status: 'failed',
            failed_at: format(new Date(), 'YYYY-MM-DD HH:mm:ss'),
            error_message: getImportErrorMessage(error),
          })
        } catch (backupError) {
          // Ignore secondary backup status failures so the original import error is preserved.
        }
        throw error
      }
    },
  },
  getters: {
    ssoEnabled: (state: any) => {
      return state.ssoEnabled
    },
    selectedSso: (state: any) => {
      return state.selectedSso
    },

    apiKeys: (state: any) => {
      return state.apiKeys
    },
    apiKeyDev: (state: any) => {
      return state.apiKeyDev
    },
    apiKeyProd: (state: any) => {
      return state.apiKeyProd
    },
    apiLogs: (state: any) => {
      return state.apiLogs
    },
    dataSettings: (state: any) => {
      return state.dataSettings
    },
    hasDirectorySyncSettings: (state: any) => {
      return state.hasDirectorySyncSettings
    },
    directorySyncSettings: (state: any) => {
      return state.directorySyncSettings
    },
    resultsSimulate: (state: any) => {
      return state.resultsSimulate
    },
    membersSimulate: (state: any) => {
      return state.membersSimulate
    },
    membersSimulateAll: (state: any) => {
      return state.membersSimulateAll
    },
    pagesimulate: (state: any) => {
      return state.pagesimulate
    },
    inputSearchSimulate: (state: any) => {
      return state.inputSearchSimulate
    },
    loadingSimulateTable: (state: any) => {
      return state.loadingSimulateTable
    },
    resultsView: (state: any) => {
      return state.resultsView
    },
    membersView: (state: any) => {
      return state.membersView
    },
    membersViewAll: (state: any) => {
      return state.membersViewAll
    },
    pageview: (state: any) => {
      return state.pageview
    },
    inputSearchView: (state: any) => {
      return state.inputSearchView
    },
    loadingViewTable: (state: any) => {
      return state.loadingViewTable
    },
    reviewUpdates: (state: any) => {
      return state.reviewUpdates
    },
    reviewUpdatesAll: (state: any) => {
      return state.reviewUpdatesAll
    },
    reviewUpdatesLoading: (state: any) => {
      return state.reviewUpdatesLoading
    },
    reviewUpdatesTotal: (state: any) => {
      return state.reviewUpdatesTotal
    },
    reviewUpdatesPage: (state: any) => {
      return state.reviewUpdatesPage
    },
    reviewUpdatesByKey: (state: any) => {
      return state.reviewUpdatesByKey
    },
    pageReviewUpdates: (state: any) => {
      return state.pageReviewUpdates
    },
    reviewUpdatesIgnorePendingStatus: (state: any) => {
      return state.pageReviewUpdates
    },
    importMembersTableDataGroups: (state: any) => {
      return state.importMembersTableDataGroups
    },
    importMembersTableDataGroupsTotal: (state: any) => {
      return state.importMembersTableDataGroupsTotal
    }
  }
}
